/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { toNumber } from 'lodash'

import { AjaxRequestError } from '@modules/ui-types'
import type { Url } from '@modules/ui-types'
import { del, get, patch, post, put } from '@modules/utils/ajax'
import type { AjaxConfig, AjaxResult } from '@modules/utils/ajax'

import { captureApmError, startHttpTransaction } from '../../../modules/utils/apm'
import { reqIdFactory } from '../../lib/reqId'
import requiredParam from '../../lib/requiredParam'
import { ASYNC_REQUEST_RESPONSE, RESET_ASYNC_REQUEST } from '../../constants/actions'
import { getAsyncRequestState } from '../../reducers/asyncRequests'

import type { ThunkAction, AsyncAction, Action } from '@/types/redux'

const methods = { get, post, put, delete: del, patch }

interface AsyncRequestSpec<TAction extends string> {
  type: TAction
  reqId: string
  meta?: { [key: string]: any }
}

interface AsyncRequestActions<TAction extends string, TPayload> {
  start: () => AsyncAction<TAction, never>
  // In an ideal world, we would require errors to always be an `Error` or a subclass
  failed: (error: Error | string) => AsyncAction<TAction, Error | string>
  success: (response: TPayload) => AsyncAction<TAction, TPayload>
}

export type Method =
  | 'get'
  | 'post'
  | 'put'
  | 'delete'
  | 'patch'
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'DELETE'
  | 'PATCH'

interface AsyncParams<TAction extends string> {
  type: TAction
  method?: Method
  url: Url
  meta?: { [key: string]: any }
  crumbs?: string[]
  payload?: unknown | string
  requestSettings?: AjaxConfig['settings']
  responseMapper?: (payload: AjaxResult) => unknown
  abortIfInProgress?: boolean
  handleUnauthorized?: boolean
  includeHeaders?: boolean
  resetApmTransaction?: boolean
  maxResponseSize?: number
  maxCompressedResponseSize?: number
}

export default function asyncRequest<TAction extends string, TPayload = unknown>({
  type = requiredParam(`type`),
  method = `get`,
  url = requiredParam(`url`),
  meta = {},
  crumbs = [],
  payload,
  requestSettings = {},
  responseMapper = defaultResponseMapper,
  abortIfInProgress = false,
  handleUnauthorized = false,
  includeHeaders = false,
  resetApmTransaction = true,
  maxResponseSize,
  maxCompressedResponseSize,
}: AsyncParams<TAction>): ThunkAction<Promise<AsyncAction<TAction, TPayload>>> {
  const reqIdBuilder = reqIdFactory(type)
  const reqId = reqIdBuilder(...crumbs)
  const { start, failed, success } = asyncRequestActions<TAction, TPayload>({ reqId, type, meta })

  return (dispatch, getState) => {
    if (abortIfInProgress) {
      const requestState = getAsyncRequestState(type)(getState(), ...crumbs)

      if (requestState.inProgress) {
        return Promise.resolve()
      }
    }

    dispatch(start())

    const lowerMethod = method.toLowerCase()
    const isGet = lowerMethod === `get`
    const request = methods[lowerMethod]
    const options: AjaxConfig = {
      settings: {
        ...requestSettings,
        response: {
          ...requestSettings.response,
          fullStatus: true,
        },
        maxResponseSize,
        maxCompressedResponseSize,
      },
      includeHeaders: true,
    }
    const params = isGet ? [url, options] : [url, payload, options]

    const httpTransaction = startHttpTransaction(method, url)

    return request(...params)
      .then((response) => {
        dispatch(asyncRequestResponse(response, responseMapper))

        const { headers, status } = response
        const successAction = success(responseMapper(response) as TPayload)

        if (includeHeaders) {
          Object.assign(successAction.meta, headers)
        }

        if (resetApmTransaction && httpTransaction) {
          httpTransaction.addLabels({ status_code: toNumber(status) })
          httpTransaction.end()
        }

        return dispatch(successAction)
      })
      .catch((error) => {
        if (error instanceof AjaxRequestError) {
          captureApmError(
            new Error(
              `${lowerMethod} ${url} failed: [${error.message}] [${error.response.status}] [${error.response.statusText}] [${reqId}]`,
            ),
          )
        } else {
          captureApmError(new Error(`${lowerMethod} ${url} failed: [${error.message}] [${reqId}]`))
        }

        httpTransaction?.addLabels({ status_code: toNumber(error?.response?.status) })
        httpTransaction?.end()

        const failAction = failed(error)

        if (handleUnauthorized) {
          failAction.meta.handleUnauthorized = true
        }

        dispatch(asyncRequestFailureResponse(failAction))
        dispatch(failAction)
        throw error
      })
  }
}

export function asyncRequestActions<TAction extends string, TPayload>(
  shape: AsyncRequestSpec<TAction>,
): AsyncRequestActions<TAction, TPayload> {
  return {
    start: () => asyncRequestStart(shape),
    failed: (error) => asyncRequestFailed(shape, error),
    success: (response) => asyncRequestSuccess(shape, response),
  }
}

export function resetAsyncRequest(
  type: string,
  crumbs: string[] = [],
): Action<typeof RESET_ASYNC_REQUEST> {
  const reqIdBuilder = reqIdFactory(type)
  const reqId = reqIdBuilder(...crumbs)

  return {
    type: RESET_ASYNC_REQUEST,
    meta: { reqId },
  }
}

function asyncRequestShape<T extends string>(
  state: 'started' | 'failed' | 'success',
  { type, reqId, meta = {} }: AsyncRequestSpec<T>,
) {
  return {
    type,
    meta: { ...meta, reqId, state },
  }
}

function asyncRequestStart<T extends string>(shape: AsyncRequestSpec<T>): AsyncAction<T, never> {
  return asyncRequestShape(`started`, shape)
}

function asyncRequestFailed<T extends string>(
  shape: AsyncRequestSpec<T>,
  payload: Error | string,
): AsyncAction<T, Error | string> {
  return {
    ...asyncRequestShape(`failed`, shape),
    payload,

    // kept for legacy reasons; eventually Start/Failed/Success merge into one
    error: true,
  }
}

function asyncRequestSuccess<TAction extends string, TPayload>(
  shape: AsyncRequestSpec<TAction>,
  payload: TPayload,
): AsyncAction<TAction, TPayload> {
  return {
    ...asyncRequestShape(`success`, shape),
    payload,
  }
}

export function asyncRequestResponse<TPayload>(
  response: AjaxResult,
  responseMapper: (response: AjaxResult) => AjaxResult | TPayload = defaultResponseMapper,
): AsyncAction<typeof ASYNC_REQUEST_RESPONSE, AjaxResult | TPayload> {
  return {
    type: ASYNC_REQUEST_RESPONSE,
    payload: responseMapper(response),
    meta: { response },
  }
}

export function asyncRequestFailureResponse<TAction extends string>(
  payload: Action<TAction>,
): AsyncAction<typeof ASYNC_REQUEST_RESPONSE, Action<TAction>> {
  return {
    type: ASYNC_REQUEST_RESPONSE,
    error: true,
    payload,
    meta: { response: payload },
  }
}

function defaultResponseMapper<TPayload>(response: AjaxResult): AjaxResult | TPayload {
  if (response.blob) {
    // blob consumers expect to be able to look at `blob` and `blobUrl` fields
    return response
  }

  return response.body
}
